import React from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { CommunityActivityTrend } from "lib/api/community";
import type { CommunityUiStrings } from "i18n/communityUi";
import SectionShell from "../SectionShell";
import {
  SectionEmptyState,
  SectionErrorState,
  SectionLoadingState,
} from "../SectionStates";
import { formatCommunityDate } from "../utils";

interface ActivityTrendProps {
  query: UseQueryResult<CommunityActivityTrend>;
  labels: CommunityUiStrings;
  compactNumberFormatter: Intl.NumberFormat;
  dateFormatter: Intl.DateTimeFormat;
}

interface TooltipPayloadRow {
  color?: string;
  dataKey?: string;
  value?: number;
}

/**
 * Stacked activity chart for contributions, analysis, and governance work.
 */
const ActivityTrend: React.FC<ActivityTrendProps> = ({
  query,
  labels,
  compactNumberFormatter,
  dateFormatter,
}) => {
  return (
    <SectionShell
      title={labels.activityTrendTitle}
      subtitle={labels.activityTrendSubtitle}
      contentClassName="space-y-4"
    >
      {query.isPending ? (
        <SectionLoadingState message={labels.loading} />
      ) : query.isError ? (
        <SectionErrorState
          message={labels.sectionError}
          retryLabel={labels.retry}
          onRetry={() => {
            void query.refetch();
          }}
          isRetrying={query.isRefetching}
        />
      ) : query.data ? (
        query.data.buckets.some((bucket) => bucket.total > 0) ? (
          <div
            role="img"
            aria-label={labels.activityTrendSubtitle}
            className="h-[22rem] w-full rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/20 p-3 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/10"
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={query.data.buckets.map((bucket) => ({
                  ...bucket,
                  displayDate: formatCommunityDate(bucket.date, dateFormatter),
                }))}
                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
              >
                <defs>
                  <linearGradient id="community-contributions" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.45} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="community-analysis" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#0f766e" stopOpacity={0.45} />
                    <stop offset="95%" stopColor="#0f766e" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="community-governance" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#d97706" stopOpacity={0.45} />
                    <stop offset="95%" stopColor="#d97706" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                <XAxis
                  dataKey="displayDate"
                  minTickGap={18}
                  tick={{ fill: "currentColor", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tickFormatter={(value) => compactNumberFormatter.format(Number(value))}
                  tick={{ fill: "currentColor", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                />
                <Tooltip
                  content={({ active, label, payload }) => {
                    if (!active || !payload || payload.length === 0) {
                      return null;
                    }

                  const rows = payload as readonly TooltipPayloadRow[];
                    const labelByKey: Record<string, string> = {
                      contributions: labels.activityLayerContributions,
                      analysis: labels.activityLayerAnalysis,
                      governance: labels.activityLayerGovernance,
                    };

                    return (
                      <div className="rounded-xl border border-eoe-border bg-white/95 p-3 shadow-lg dark:border-eoe-dark-border dark:bg-eoe-dark-bg-card/95">
                        <p className="mb-2 text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                          {label}
                        </p>
                        <div className="space-y-1.5">
                          {rows.map((row) => (
                            <div key={String(row.dataKey)} className="flex items-center gap-2 text-sm">
                              <span
                                className="size-2 rounded-full"
                                style={{ backgroundColor: row.color || "#2563eb" }}
                              />
                              <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                {labelByKey[String(row.dataKey)] || String(row.dataKey)}
                              </span>
                              <span className="ml-auto font-medium text-eoe-text dark:text-eoe-dark-text">
                                {compactNumberFormatter.format(Number(row.value || 0))}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="contributions"
                  stackId="community"
                  stroke="#2563eb"
                  fill="url(#community-contributions)"
                  name={labels.activityLayerContributions}
                />
                <Area
                  type="monotone"
                  dataKey="analysis"
                  stackId="community"
                  stroke="#0f766e"
                  fill="url(#community-analysis)"
                  name={labels.activityLayerAnalysis}
                />
                <Area
                  type="monotone"
                  dataKey="governance"
                  stackId="community"
                  stroke="#d97706"
                  fill="url(#community-governance)"
                  name={labels.activityLayerGovernance}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <SectionEmptyState title={labels.emptyActivityTrend} />
        )
      ) : (
        <SectionLoadingState message={labels.loading} />
      )}
    </SectionShell>
  );
};

export default ActivityTrend;

